import React, { createContext, useContext, ReactNode } from 'react';
import { createAssetDownloaderInstance } from '@/contexts/DownloadAssetContext/createAssetDownloaderInstance';
import { IS_WEB } from '@/utils/platform';
import { logError } from '@/utils/logHandling';
import { toWebAssetUrl } from '@/utils/webAssetUrl';

const emptyDownloader = {
  state: {
    progress: 0,
    isDownloading: false,
    isComplete: false,
    error: null,
    downloaded: 0,
    total: 0,
  },
  downloadAssets: async () => {},
  getLocalUri: async (url: string) => toWebAssetUrl(url),
  hasFreeStorage: async () => true,
  stopDownload: () => {},
};

// Create the context with the same interface as createAssetDownloaderInstance
const DownloadContext = createContext<ReturnType<
  typeof createAssetDownloaderInstance
> | null>(null);

interface DownloadProviderProps {
  children: ReactNode;
}

export function DownloadProvider({ children }: DownloadProviderProps) {
  // Single instance for the entire app
  const downloadManager = IS_WEB
    ? emptyDownloader
    : createAssetDownloaderInstance();

  return (
    <DownloadContext.Provider value={downloadManager}>
      {children}
    </DownloadContext.Provider>
  );
}

// Main hook to use the shared download manager
export function useAssetDownloader() {
  const context = useContext(DownloadContext);
  if (!context) {
    logError('useAssetDownloader must be used within a DownloadProvider');

    if (IS_WEB) {
      return emptyDownloader;
    }

    throw new Error(
      'useAssetDownloader must be used within a DownloadProvider',
    );
  }
  return context;
}
